import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { VacationRequest, VacationRequestStatus } from '../../types';
import { VacationStatusBadge } from './VacationStatusBadge';
import { formatShortDate } from '../../utils/formatters';

interface VacationRequestCardProps {
  request: VacationRequest;
  isSubmitting?: boolean;
  onCancel?: (request: VacationRequest) => void;
}

/**
 * Single reusable row for a member's vacation request. The visible state
 * (status badge, whether the Cancel action shows) is driven entirely by the
 * request's `status` — Cancel is only offered while the request is PENDING.
 */
export const VacationRequestCard: React.FC<VacationRequestCardProps> = ({
  request,
  isSubmitting,
  onCancel,
}) => {
  const { t } = useTranslation('vacations');
  const canCancel = request.status === VacationRequestStatus.PENDING;

  return (
    <div
      className="flex items-start justify-between gap-4 py-4"
      data-testid={`vacation-card-${request.id}`}
    >
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-semibold text-foreground">
          {formatShortDate(request.startDate)} – {formatShortDate(request.endDate)}
        </p>
        <p className="text-sm text-muted-foreground">
          {t('review.workingDays')}: {request.workingDays}
        </p>
        {request.notes && (
          <p className="text-sm text-muted-foreground">
            {t('review.notes')}: {request.notes}
          </p>
        )}
        {request.status === VacationRequestStatus.REJECTED && request.reviewerComments && (
          <p className="text-sm text-destructive">
            {t('card.reviewerComment')}: {request.reviewerComments}
          </p>
        )}
        {request.status === VacationRequestStatus.CANCELLED && request.cancellationReason && (
          <p className="text-sm text-muted-foreground">
            {t('view.cancellationReason')}: {request.cancellationReason}
          </p>
        )}
      </div>
      <div className="flex flex-shrink-0 items-center gap-3">
        <VacationStatusBadge status={request.status} />
        {canCancel && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCancel?.(request)}
            disabled={isSubmitting}
            data-testid={`vacation-card-cancel-${request.id}`}
          >
            {t('actions.cancel')}
          </Button>
        )}
      </div>
    </div>
  );
};
